import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { BarChart3, Star } from 'lucide-react';
import type { OverviewCompare, OverviewFlags } from '../api/overviewApi';
import { LockedSection } from './LockedSection';
import styles from './CompareSection.module.css';

interface CompareSectionProps {
  flags: OverviewFlags;
  data: OverviewCompare;
}

export function CompareSection({ flags, data }: CompareSectionProps) {
  const navigate = useNavigate();

  if (!flags.hasStrategy && !flags.hasSEO) {
    return (
      <LockedSection
        icon={BarChart3}
        ctaColor="accent"
        headline={
          flags.strategyInProgress
            ? 'Complete your strategy to unlock neighborhood comparison'
            : 'Create a strategy to unlock neighborhood comparison'
        }
        sub="See how your reviews compare to nearby practices and which demographics are most concentrated in your patient zip codes."
        cta={flags.strategyInProgress ? 'Complete Strategy →' : 'Create Strategy →'}
        onCtaClick={() => navigate('/strategy/builder')}
      />
    );
  }

  const competitors = [...data.competitors].sort((a, b) => (b.reviews ?? 0) - (a.reviews ?? 0));
  const maxReviews = Math.max(1, ...competitors.map((c) => c.reviews ?? 0));
  const maxZipPct = Math.max(1, ...data.zips.map((z) => z.pctPatients));

  const topCompetitor = competitors.find((c) => !c.isYou);
  const yourReviews = competitors.find((c) => c.isYou)?.reviews ?? 0;
  const reviewGap =
    topCompetitor && topCompetitor.reviews !== null ? topCompetitor.reviews - yourReviews : null;

  return (
    <div className={styles.grid}>
      {/* Review comparison */}
      <div className={styles.card}>
        <div className={styles.label}>Nearby Practice Reviews</div>
        <div className={styles.sublabel}>Sourced from Google · updated weekly via Places API</div>
        <div className={styles.competitors}>
          {competitors.length === 0 ? (
            <div className={styles.empty}>Run a strategy to pull nearby practices.</div>
          ) : (
            competitors.map((c, i) => (
              <div key={i} className={clsx(styles.competitor, c.isYou && styles.competitorYou)}>
                <div className={styles.competitorHeader}>
                  <div className={clsx(styles.competitorName, c.isYou && styles.competitorNameYou)}>
                    {c.name}
                  </div>
                  <div className={styles.competitorMeta}>
                    <span className={styles.star}>
                      <Star size={12} fill="currentColor" aria-hidden />{' '}
                      {c.rating?.toFixed(1) ?? '—'}
                    </span>
                    <span className={styles.reviews}>{c.reviews ?? 0}</span>
                  </div>
                </div>
                <div className={styles.bar}>
                  <div
                    className={clsx(styles.barFill, c.isYou && styles.barFillYou)}
                    style={{ width: `${((c.reviews ?? 0) / maxReviews) * 100}%` }}
                  />
                </div>
                {c.isYou && reviewGap !== null && reviewGap > 0 && topCompetitor && (
                  <div className={styles.gap}>
                    Gap to {topCompetitor.name}:{' '}
                    <span className={styles.gapNum}>{reviewGap} reviews</span> · auto-requesting
                    after each visit
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Patient geography */}
      <div className={styles.card}>
        <div className={styles.label}>Where Your Patients Come From</div>
        <div className={styles.sublabel}>
          Patient zip distribution · census demographics per zip (ACS)
        </div>
        <div className={styles.zips}>
          {data.zips.length === 0 ? (
            <div className={styles.empty}>
              Upload patient zip data in Strategy to see distribution.
            </div>
          ) : (
            data.zips.map((z) => (
              <div key={z.zip}>
                <div className={styles.zipHeader}>
                  <div>
                    <span className={styles.zipCode}>{z.zip}</span>
                    <span className={styles.zipLabel}>{z.label}</span>
                  </div>
                  <div className={styles.zipMeta}>
                    <span>{z.pctPatients.toFixed(1)}% of patients</span>
                    {z.pctHispanic !== null && (
                      <span
                        className={z.pctHispanic > 40 ? styles.metaHighlight : styles.metaMuted}
                      >
                        {z.pctHispanic}% Hispanic
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.zipBar}>
                  <div
                    className={styles.zipBarFill}
                    style={{ width: `${(z.pctPatients / maxZipPct) * 100}%` }}
                  />
                </div>
              </div>
            ))
          )}
        </div>
        {data.gapInsight && (
          <div className={styles.insight}>
            <div className={styles.insightTitle}>Largest opportunity gap</div>
            <div className={styles.insightBody}>{data.gapInsight}</div>
          </div>
        )}
        {flags.hasStrategy && (
          <button type="button" className={styles.link} onClick={() => navigate('/strategy')}>
            Full demographic analysis in Strategy →
          </button>
        )}
      </div>
    </div>
  );
}
